export const meta = {
	slug: 'hopalong-family',
	title: 'Four Hopalong variants, side by side',
	tag: 'Attractors',
	excerpt:
		'Barry Martin’s 1981 Hopalong map (the Skull attractor) in four live Classical Chaos variants: classic, positive, additive, and sinusoidal, so you can see what changes when the rule changes.',
	dateLabel: 'Aug 2026',
	readMinutes: 8,
	thumbSlug: 'hopalong_attractor',
	featured: false,
	order: 20,
}


Barry Martin found the Hopalong map in 1981. People also call it the Skull attractor. Unlike Lorenz, there is no smooth trail of rates. You take a point `(x, y)`, run a short formula, get the next point, and pile those points into a cloud.

The rule is tiny and the cloud still rearranges when one term changes. Below are the four versions Classical Chaos ships, all live at once so the silhouettes stay comparable.

<VizEmbedGrid>
  <VizEmbed slug="hopalong_attractor" label="Classic" particles={16000} />
  <VizEmbed slug="hopalong_attractor_positive" label="Positive" particles={16000} />
  <VizEmbed slug="hopalong_attractor_additive" label="Additive" particles={16000} />
  <VizEmbed slug="hopalong_attractor_sinusoidal" label="Sinusoidal" particles={16000} />
</VizEmbedGrid>

## Map, not flow

Each new point comes only from the last one. No integrating along a curve. Start near a seed, keep iterating, and the silhouette that piles up is the attractor.

Classic Hopalong uses knobs `a`, `b`, `c`. The positive / additive / sinusoidal trio keep the same outer shape and only rewrite the middle of the x-step. Either way: many steps from a seed, then a colored cloud.

## Classic

Defaults on the classic stage sit near `a = 1.4`, `b ≈ 5.16`, `c ≈ 2.74`, seed `(0.03, 0.01)`. Color walks through hue as the points accumulate so the cloud doesn’t go flat.

Martin’s classic form:

<BlockMath math={'x\\prime = y - \\mathrm{sgn}(x)\\,\\sqrt{|b x - c|}'} />
<BlockMath math={'y\\prime = a - x'} />

```ts
const nextX = y - Math.sign(x) * Math.sqrt(Math.abs(b * x - c))
const nextY = a - x
x = nextX
y = nextY
```

On the [full classic stage](/hopalong_attractor) you can shove those knobs around (including a wild preset near `a ≈ −11`) and the cloud rebuilds from the seed.


## Three cousins: one term changes

Positive, additive, and sinusoidal keep `y' = a − x` and only rewrite the x-step’s middle piece.

**Positive** flips the radical to addition, still signed:

<BlockMath math={'x\\prime = y + \\mathrm{sgn}(x)\\,\\sqrt{|b x - c|}'} />
<BlockMath math={'y\\prime = a - x'} />

**Additive** drops the sign: always add the square root.

<BlockMath math={'x\\prime = y + \\sqrt{|b x - c|}'} />
<BlockMath math={'y\\prime = a - x'} />

**Sinusoidal** swaps the radical for a sine:

<BlockMath math={'x\\prime = y + \\sin(b x - c)'} />
<BlockMath math={'y\\prime = a - x'} />

Because sine stays between −1 and 1, small `b` stays near-periodic (thin rings). The stage defaults sit nearer `a = 2.5`, `b = 4`, `c = 0.2` so the cloud fills; classic-style `b ≈ 5` also works.

Classic keeps the two-line Martin rule with `sign`. The other three only disagree about the middle x-step: signed sqrt, unsigned sqrt, or sine.

## Looking at the cards

All four are point clouds from iterating a map. Tip points often read brighter. Serial comparison fails here: the last silhouette sticks in memory and erases the difference. The grid keeps all four visible at once.

<Callout to="/hopalong_attractor">
	<strong>Goto full canvas</strong>
	Classic stage first: twist `a`, `b`, `c`, try the presets. Or open full on any card above for that variant’s own knobs.
</Callout>
